import { occFetch, occUrl } from "~/app/api/occ/occFetch";
import { getCsrfValue } from "~/common/utils/ApiHelper";
import { resolveApiUrl } from "~/utils/url/urlResolver";
import { clearSessionRuntimeState } from "~/domains/user/sessionRuntime";
import { completeAccountContextTransition, prepareAccountContextTransition } from "~/app/accountContextTransition";

export interface AccountSelection {
  accountId: string;
  salesOrg: string;
  endCustomerCode?: string;
}

function occQuoteAccountUrl(data: AccountSelection) {
  const basePath = occUrl("/quote/account");
  return [basePath, data.accountId, data.salesOrg, data.endCustomerCode].filter(Boolean).join("/");
}

export function sendData(data: AccountSelection): Promise<void> {
  const url = occQuoteAccountUrl(data);

  return occFetch(
    url,
    {
      method: "POST",
    },
    { accountContext: "omit" },
  ).then((response) => {
    if (!response.ok) {
      throw new Error(response.status + "");
    }
  });
}

export function synchronizeStorefrontAccountSession(data: AccountSelection): Promise<void> {
  return fetch(resolveApiUrl("/my-account/session/account-context"), {
    method: "POST",
    credentials: "same-origin",
    headers: {
      "Content-Type": "application/json",
      CSRFToken: getCsrfValue(),
    },
    body: JSON.stringify({
      soldTo: data.accountId,
      salesOrg: data.salesOrg,
      ...(data.endCustomerCode ? { endCustomer: data.endCustomerCode } : {}),
    }),
  }).then((response) => {
    if (!response.ok) {
      throw new Error(response.status + "");
    }
  });
}

export async function selectAccount(data: AccountSelection): Promise<void> {
  if (!data.salesOrg) {
    throw new Error("Account selection requires a sales organization.");
  }

  await sendData(data);
  // OCC success is the point of no return. Clear all client endpoint state before the storefront
  // session sync; a sync failure therefore fails closed with no old-account data left to render.
  await prepareAccountContextTransition();
  clearSessionRuntimeState();
  await synchronizeStorefrontAccountSession(data);
  await completeAccountContextTransition();
}
